import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'
import { Image, ScrollView, StyleSheet } from 'react-native'

import {
  Button,
  Icon,
  Input,
  SH1,
  Select,
  Stack,
  XStack,
  colors as zetColors,
} from '@zet/design-system'
import { BottomSheet } from '@zet/design-system/bottomSheet'
import Pressable from '@zet/design-system/pressable'
import { i18n } from '@zet/localization'
import { DEVICE_HEIGHT } from '@zet/utils/Constants'
import { useFormContext } from 'react-hook-form'

import { useAutoComplete } from '../../../../api/autoComplete/hooks/useAutoComplete'
import { useApiAction } from '../../../../api/useApiAction'
import { IOcField } from '../../../../utils/types'

const Dropdown = forwardRef(
  (
    {
      label = 'Select Dropdown',
      dropdownMode = 'single',
      isSearchable = true,
      onDismiss,
      selectedValue,
      onSelect,
      setDropDownVisible,
      dropDownVisible,
      options,
      fieldId,
      ocFieldCode,
      fieldDependency = '',
      autoCompleteLength,
    },
    ref
  ) => {
    const [searchedValue, setSearchedValue] = useState('')
    const [selectedTempValue, setSelectedTempValue] = useState({
      value: '',
      displayText: '',
    })
    const [dependencyValue, setDependencyValue] = useState('')
    const { data: list } = useApiAction({ skipInitFetch: true })
    const { data, refetch: getAutoComplete } = useAutoComplete(
      searchedValue,
      fieldId,
      ocFieldCode,
      dependencyValue,
      list?.metadata?.leadId ?? ''
    )

    const form = useFormContext()

    useEffect(() => {
      if (dropDownVisible) {
        if (fieldDependency !== '') {
          const fields = form?.getValues()
          if (Object.prototype.hasOwnProperty.call(fields, fieldDependency)) {
            const dependentOptions = list?.layout?.ocFields?.find(
              (element: IOcField) => element.id === fieldDependency.toString()
            )

            const fieldOption = dependentOptions?.fieldOptions?.find(
              (element: any) =>
                element.optionId === fields[fieldDependency]?.toString()
            )

            if (fieldOption) {
              setDependencyValue(fieldOption.optionValue ?? '')
            } else {
              setDependencyValue(fields[fieldDependency] ?? '')
            }
          }
        }
      }
    }, [searchedValue])

    useImperativeHandle(ref, () => ({
      getFieldValue: (value: string) => {
        const select = data?.results?.find(
          (item: any) => item.optionValue === value
        )
        return select
      },
    }))

    useEffect(() => {
      const regex = new RegExp(`(.*[a-z0-9]){${autoCompleteLength}}`, 'i')
      if (regex.test(searchedValue)) {
        getAutoComplete()
      }
    }, [searchedValue])

    function selectValue(value: string, displayText: string) {
      if (!isSearchable) {
        setDropDownVisible(false)
        onSelect(value, displayText)
      }
      setSelectedTempValue({ value, displayText })
    }

    const onSubmit = () => {
      setDropDownVisible(false)
      onSelect(selectedTempValue.value, selectedTempValue.displayText)
    }

    const Header = () => (
      <XStack className="items-center">
        <Stack>
          <Icon type="WorkOutlined" size={24} />
        </Stack>
        <SH1 className="ml-2" numberOfLines={1} ellipsizeMode="tail">
          {label}
        </SH1>
        <Pressable
          onPress={() => setDropDownVisible(false)}
          className="ml-auto"
        >
          <Icon size={24} type="Close" />
        </Pressable>
      </XStack>
    )

    return (
      <Stack>
        <BottomSheet
          visible={dropDownVisible}
          onDismiss={onDismiss}
          header={<Header />}
          snapPoints={isSearchable ? [DEVICE_HEIGHT - 200, 500] : undefined}
        >
          <ScrollView style={{ height: isSearchable && 370 }}>
            <Stack>
              {isSearchable ? (
                <Stack className="mx-3 mt-2">
                  <Input
                    placeholder={'search'}
                    onChangeText={(text) => {
                      setSearchedValue(text)
                    }}
                    autoCapitalize={'none'}
                    spellCheck={false}
                    // keyboardType="number-pad"
                    // iconLeft={<Icon type="LocationOutlined" />}
                  />
                </Stack>
              ) : (
                <></>
              )}
            </Stack>
            <Stack className="px-3 pb-3">
              <Stack className="" style={{ height: 300 }}>
                <Select
                  value={selectedValue}
                  mode={dropdownMode}
                  onChange={selectValue}
                >
                  {(isSearchable ? data?.results.slice(0, 20) : options)?.map(
                    ({ optionValue, optionId, icon }) => (
                      <Select.Option
                        key={optionId}
                        name={optionId}
                        displayText={optionValue}
                        selectedValue={selectedTempValue.value ?? selectedValue}
                        optionContainerStyle="h-6 mt-3 w-full border border-grays-40 justify-center rounded-lg py-4"
                        showToggle={true}
                        iconColor={{
                          active: zetColors.primary[50],
                          inActive: zetColors.grays[60],
                        }}
                        backgroundColor={{
                          active: 'bg-primary-10',
                          inActive: 'bg-grays-20',
                        }}
                        textColor={{
                          active: 'text-primary-200',
                          inActive: 'text-grays-80',
                        }}
                      >
                        <XStack className="w-full items-center justify-between">
                          <SH1>{optionValue}</SH1>
                          {icon && (
                            <Image
                              style={styles.logo}
                              source={{
                                uri: 'https://onecode-asset.s3.ap-south-1.amazonaws.com/zet/brandLogos/239.png',
                              }}
                            />
                          )}
                        </XStack>
                      </Select.Option>
                    )
                  )}
                </Select>
              </Stack>
            </Stack>
          </ScrollView>
          {isSearchable && data?.results && (
            <Stack className="border-grays-40 w-full border-t bg-white px-3 py-3">
              <Button
                className="w-full"
                size="lg"
                onPress={onSubmit}
                disabled={Boolean(!selectedValue && !selectedTempValue.value)}
              >
                {i18n.t('tds.select')}
              </Button>
            </Stack>
          )}
        </BottomSheet>
      </Stack>
    )
  }
)

export default Dropdown

const styles = StyleSheet.create({
  logo: {
    resizeMode: 'contain',
    width: 100,
    height: 35,
    marginRight: 12,
  },
})
